import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  AlarmRequest,
  AlarmTableItem,
  createAlarm,
  deleteAlarmInfo,
  getAlarm,
  getAllAlarmsTable,
  GetAlarmResponse,
  updateAlarm,
} from '@app/api/alarm.api'

import type { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

type AlarmState = {
  alarmsTableData: Record<string, AlarmTableItem>
  alarmsListStatus: Status
  alarmDetailsData: Record<string, GetAlarmResponse>
  alarmDetailsStatusById: Record<string, Status>
  createAlarmStatus: Status
  updateAlarmStatus: Status
  deleteAlarmStatus: Status
}

const initialState: AlarmState = {
  alarmsTableData: {},
  alarmsListStatus: 'idle',
  alarmDetailsData: {},
  alarmDetailsStatusById: {},
  createAlarmStatus: 'idle',
  updateAlarmStatus: 'idle',
  deleteAlarmStatus: 'idle',
}

export type AlarmInfoUpdateRequest = {
  id: string
  data: AlarmRequest
}

export const getAllAlarmsTableData = createAsyncThunk<AlarmTableItem[]>(
  'alarmInfo/getAllAlarmsTableData',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllAlarmsTable()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAlarmByIdData = createAsyncThunk<GetAlarmResponse, string>(
  'alarmInfo/getAlarmById',
  async (id, { rejectWithValue }) => {
    try {
      return await getAlarm(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createAlarmData = createAsyncThunk<GetAlarmResponse, AlarmRequest>(
  'alarm/createAlarm',
  async (payload, { rejectWithValue }) => {
    try {
      return await createAlarm(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateAlarmData = createAsyncThunk<GetAlarmResponse, AlarmInfoUpdateRequest>(
  'alarm/updateAlarm',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await updateAlarm(id, data)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deleteAlarmInfoData = createAsyncThunk<GetAlarmResponse, string>(
  'alarmInfo/deleteAlarmInfo',
  async (id, { rejectWithValue }) => {
    try {
      return await deleteAlarmInfo(id)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const alarmSlice = createSlice({
  name: 'alarmInfo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllAlarmsTableData.pending, (state) => {
        state.alarmsListStatus = 'loading'
      })
      .addCase(getAllAlarmsTableData.rejected, (state) => {
        state.alarmsListStatus = 'failed'
      })
      .addCase(getAllAlarmsTableData.fulfilled, (state, action) => {
        state.alarmsTableData = Object.fromEntries(action.payload.map((alarm) => [alarm.id, alarm]))
        state.alarmsListStatus = 'succeeded'
      })
      .addCase(getAlarmByIdData.pending, (state, action) => {
        state.alarmDetailsStatusById[action.meta.arg] = 'loading'
      })
      .addCase(getAlarmByIdData.rejected, (state, action) => {
        state.alarmDetailsStatusById[action.meta.arg] = 'failed'
      })
      .addCase(getAlarmByIdData.fulfilled, (state, action) => {
        state.alarmDetailsData[action.payload.id] = action.payload
        state.alarmDetailsStatusById[action.payload.id] = 'succeeded'
      })
      .addCase(createAlarmData.pending, (state) => {
        state.createAlarmStatus = 'loading'
      })
      .addCase(createAlarmData.rejected, (state) => {
        state.createAlarmStatus = 'failed'
      })
      .addCase(createAlarmData.fulfilled, (state, action) => {
        const alarm = action.payload

        state.alarmsTableData[alarm.id] = toAlarmTableItem(alarm)
        state.alarmDetailsData[alarm.id] = alarm
        state.alarmDetailsStatusById[alarm.id] = 'succeeded'
        state.createAlarmStatus = 'succeeded'
      })
      .addCase(updateAlarmData.pending, (state) => {
        state.updateAlarmStatus = 'loading'
      })
      .addCase(updateAlarmData.rejected, (state) => {
        state.updateAlarmStatus = 'failed'
      })
      .addCase(updateAlarmData.fulfilled, (state, action) => {
        const alarm = action.payload

        state.alarmsTableData[alarm.id] = toAlarmTableItem(alarm)
        state.alarmDetailsData[alarm.id] = alarm
        state.alarmDetailsStatusById[alarm.id] = 'succeeded'
        state.updateAlarmStatus = 'succeeded'
      })
      .addCase(deleteAlarmInfoData.pending, (state) => {
        state.deleteAlarmStatus = 'loading'
      })
      .addCase(deleteAlarmInfoData.rejected, (state) => {
        state.deleteAlarmStatus = 'failed'
      })
      .addCase(deleteAlarmInfoData.fulfilled, (state, action) => {
        const id = action.payload.id

        delete state.alarmsTableData[id]
        delete state.alarmDetailsData[id]
        delete state.alarmDetailsStatusById[id]
        state.deleteAlarmStatus = 'succeeded'
      })
  },
})

const toAlarmTableItem = ({ id, name, type }: GetAlarmResponse): AlarmTableItem => ({ id, name, type })

export default alarmSlice.reducer
